import { useEffect } from 'react'
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Highlight from '@tiptap/extension-highlight'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Highlighter,
  Heading2,
  Table as TableIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// eslint-disable-next-line react-refresh/only-export-components
export const EMPTY_DOC: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
}

interface RichTextEditorProps {
  content: JSONContent | null
  editable: boolean
  onChange?: (json: JSONContent) => void
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  editable,
  onChange,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: content ?? EMPTY_DOC,
    editable,
    onUpdate: ({ editor: e }) => onChange?.(e.getJSON()),
  })

  useEffect(() => {
    editor?.setEditable(editable)
  }, [editor, editable])

  useEffect(() => {
    if (!editor) return
    const next = content ?? EMPTY_DOC
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(next)) {
      editor.commands.setContent(next, false)
    }
  }, [editor, content])

  if (!editor) return null

  return (
    <div className='flex flex-1 min-h-0 flex-col rounded-lg border border-border'>
      {editable && (
        <div className='flex flex-wrap items-center gap-1 border-b border-border bg-muted/40 p-1.5'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className={cn(
              'h-7 w-7 p-0',
              editor.isActive('bold') && 'bg-accent'
            )}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className={cn(
              'h-7 w-7 p-0',
              editor.isActive('italic') && 'bg-accent'
            )}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className={cn(
              'h-7 w-7 p-0',
              editor.isActive('underline') && 'bg-accent'
            )}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className={cn(
              'h-7 w-7 p-0',
              editor.isActive('highlight') && 'bg-accent'
            )}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          >
            <Highlighter className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className={cn(
              'h-7 w-7 p-0',
              editor.isActive('heading', { level: 2 }) && 'bg-accent'
            )}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <Heading2 className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className={cn(
              'h-7 w-7 p-0',
              editor.isActive('bulletList') && 'bg-accent'
            )}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className={cn(
              'h-7 w-7 p-0',
              editor.isActive('orderedList') && 'bg-accent'
            )}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 w-7 p-0'
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 2, cols: 2, withHeaderRow: true })
                .run()
            }
          >
            <TableIcon className='h-3.5 w-3.5' />
          </Button>
        </div>
      )}
      {editable && editor.isActive('table') && (
        <div className='flex flex-wrap items-center gap-1 border-b border-border bg-muted/20 px-1.5 py-1'>
          <span className='mr-1 text-[10px] font-medium text-muted-foreground'>
            Table:
          </span>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px]'
            onClick={() => editor.chain().focus().addRowBefore().run()}
          >
            + Row above
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px]'
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            + Row below
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px]'
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            − Row
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px]'
            onClick={() => editor.chain().focus().addColumnBefore().run()}
          >
            + Col left
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px]'
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            + Col right
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px]'
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            − Col
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px]'
            onClick={() => editor.chain().focus().mergeOrSplit().run()}
          >
            Merge/Split
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[10px] text-destructive hover:text-destructive'
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            Delete table
          </Button>
        </div>
      )}
      <EditorContent
        editor={editor}
        className={cn(
          'flex-1 min-h-[180px] overflow-y-auto px-3 py-2 text-sm',
          '[&_.ProseMirror]:min-h-[160px] [&_.ProseMirror]:outline-none',
          '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_p]:mb-1 [&_mark]:rounded [&_mark]:px-0.5',
          '[&_table]:border-collapse [&_table]:my-2',
          '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1',
          '[&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/40'
        )}
      />
    </div>
  )
}

export default RichTextEditor
